export enum Role {
    ADMINISTRATOR = 'Administrator',
    FLEET_MANAGER = 'FleetManager',
    FLEETUSER = 'Fleetuser',
    WORKSHOP_MANAGER = 'WorkshopManager',
    DISPATCHER = 'Dispatcher',
    HUMAN_RESOURCES = 'HumanResources',
    SHIPPER = 'Shipper',
    DRIVER = 'Driver',
    DRIVER_TRAINER = 'DriverTrainer',
    ANALYSIS_EXPERT = 'AnalysisExpert',
    BOOKING_EXPERT = 'BookingExpert',
    EE_DATA_ACT_USER = 'EuDataActUser',
    CROSSDOCK_USER = 'CrossdockUser',
}

export enum Service {
    PDC = 'intl-msg:roles.services.pdc',
    DIAGNOSTICS = 'intl-msg:roles.services.diagnostics',
    PERFORM = 'intl-msg:roles.services.perform',
    FLEET_MONITOR = 'intl-msg:roles.services.fleetMonitor',
    TIMED = 'intl-msg:roles.services.timed',
    SERVICE_CARE = 'intl-msg:roles.services.serviceCare',
    TACHO = 'intl-msg:roles.services.tacho',
    MARKETPLACE = 'intl-msg:roles.services.marketplace',
    EMANAGER = 'intl-msg:roles.services.eManager',
    MANNOW = 'intl-msg:roles.services.mannow',
    CHARGE_AND_GO = 'intl-msg:roles.services.chargeAndGo',

    USER_ADMIN = 'intl-msg:roles.services.userAdmin',
    DRIVER_ADMIN = 'intl-msg:roles.services.driverAdmin',
    VEHICLE_ADMIN = 'intl-msg:roles.services.vehicleAdmin',
    EU_DATA_ACT_REQUEST = 'intl-msg:roles.services.eudataactrequest',
    WEB_TMS = 'intl-msg:roles.services.WebTMS',
}

export enum Access {
    FULL = 'intl-msg:roles.description.fullAccess',
    RESTRICTED = 'intl-msg:roles.description.restrictedAccess',
    NO = 'intl-msg:roles.description.noAccess',
}

export interface RoleAccess {
    access: Access;
    details?: string;
}

const FULL: RoleAccess = { access: Access.FULL };
const RESTRICTED = (details: string): RoleAccess => {
    return { access: Access.RESTRICTED, details };
};
const NO: RoleAccess = { access: Access.NO };

export interface RoleDefinition {
    title: string;
    description: string;
    scope: string;
    services: Services;
    adminServices?: Services;
}
export type Services = Partial<Record<keyof typeof Service, RoleAccess>>;

export const RoleAccessMatrix: Record<Role, RoleDefinition> = {
    Administrator: {
        title: 'intl-msg:roles.names.rio.customer.base.FleetAdmin',
        description: 'intl-msg:roles.names.rio.customer.description.Administrator',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: FULL,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: FULL,
            MARKETPLACE: FULL,
            EMANAGER: FULL,
            MANNOW: FULL,
            CHARGE_AND_GO: FULL,
        },
        adminServices: {
            USER_ADMIN: FULL,
            DRIVER_ADMIN: FULL,
            VEHICLE_ADMIN: FULL,
            EU_DATA_ACT_REQUEST: FULL,
        },
    },
    FleetManager: {
        title: 'intl-msg:roles.names.FleetManager',
        description: 'intl-msg:roles.names.rio.customer.description.FleetManager',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: FULL,
            MARKETPLACE: FULL,
            EMANAGER: FULL,
            MANNOW: FULL,
            CHARGE_AND_GO: FULL,
        },
        adminServices: {
            USER_ADMIN: NO,
            DRIVER_ADMIN: FULL,
            VEHICLE_ADMIN: FULL,
            EU_DATA_ACT_REQUEST: FULL,
        },
    },
    Fleetuser: {
        title: 'intl-msg:roles.names.rio.customer.base.FleetUser',
        description: 'intl-msg:roles.names.rio.customer.description.FleetUser',
        scope: 'intl-msg:roles.description.tagBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: FULL,
            MARKETPLACE: FULL,
            EMANAGER: RESTRICTED('intl-msg:roles.description.restriction.cannotControlCharging'),
            MANNOW: NO,
            CHARGE_AND_GO: RESTRICTED('intl-msg:roles.description.restriction.onlyChargingSessionAndCards'),
            // Can only see charging sessions and charging cards
        },
    },
    WorkshopManager: {
        title: 'intl-msg:roles.names.WorkshopManager',
        description: 'intl-msg:roles.names.rio.customer.description.WorkshopManager',
        scope: 'intl-msg:roles.description.tagBased',
        services: {
            PDC: FULL,
            DIAGNOSTICS: FULL,
            PERFORM: NO,
            FLEET_MONITOR: RESTRICTED('intl-msg:roles.description.restriction.viewMap'),
            TIMED: NO,
            SERVICE_CARE: FULL,
            TACHO: NO,
            MARKETPLACE: RESTRICTED('intl-msg:roles.description.restriction.cannotBookServices'),
            EMANAGER: RESTRICTED('intl-msg:roles.description.restriction.cannotControlCharging'),
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    Dispatcher: {
        title: 'intl-msg:roles.names.Dispatcher',
        description: 'intl-msg:roles.names.rio.customer.description.Dispatcher',
        scope: 'intl-msg:roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: FULL,
            TIMED: FULL,
            SERVICE_CARE: FULL,
            TACHO: RESTRICTED('intl-msg:roles.description.restriction.dispatcher.tachograph'),
            MARKETPLACE: RESTRICTED('intl-msg:roles.description.restriction.cannotBookServices'),
            EMANAGER: FULL,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    HumanResources: {
        title: 'intl-msg:roles.names.HumanResources',
        description: 'intl-msg:roles.names.rio.customer.description.HumanResources',
        scope: 'intl-msg:roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: FULL,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: RESTRICTED('intl-msg:roles.description.restriction.humanresources.tachograph'),
            MARKETPLACE: RESTRICTED('intl-msg:roles.description.restriction.cannotBookServices'),
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    Shipper: {
        title: 'intl-msg:roles.names.Shipper',
        description: 'intl-msg:roles.names.rio.customer.description.Shipper',
        scope: 'intl-msg:roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: RESTRICTED('intl-msg:roles.description.restriction.viewMap'),
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    Driver: {
        title: 'intl-msg:roles.names.rio.orderconnect.Driver',
        description: 'intl-msg:roles.names.rio.customer.description.Driver',
        scope: 'intl-msg:roles.description.tagBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: RESTRICTED('intl-msg:roles.description.restriction.cannotBookServices'),
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    DriverTrainer: {
        title: 'intl-msg:roles.names.rio.perform.CoDriverTrainer',
        description: 'intl-msg:roles.names.rio.customer.description.CoDriverTrainer',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: RESTRICTED('intl-msg:roles.description.restriction.cannotBookServices'),
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    BookingExpert: {
        title: 'intl-msg:roles.names.rio.mds.BookingExpert',
        description: 'intl-msg:roles.names.rio.mds.description.BookingExpert',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: FULL,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    AnalysisExpert: {
        title: 'intl-msg:roles.names.rio.mds.AnalysisExpert',
        description: 'intl-msg:roles.names.rio.mds.description.AnalysisExpert',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: FULL,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
    },
    EuDataActUser: {
        title: 'intl-msg:roles.names.rio.thirdparty.EuDataActUser',
        description: 'intl-msg:roles.names.rio.thirdparty.description.EuDataActUser',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
        adminServices: {
            EU_DATA_ACT_REQUEST: FULL,
        },
    },
    CrossdockUser: {
        title: 'intl-msg:roles.names.rio.crossdock.CrossdockUser',
        description: 'intl-msg:roles.names.rio.crossdock.description.CrossdockUser',
        scope: 'intl-msg:roles.description.accountBased',
        services: {
            PDC: NO,
            DIAGNOSTICS: NO,
            PERFORM: NO,
            FLEET_MONITOR: NO,
            TIMED: NO,
            SERVICE_CARE: NO,
            TACHO: NO,
            MARKETPLACE: NO,
            EMANAGER: NO,
            MANNOW: NO,
            CHARGE_AND_GO: NO,
        },
        adminServices: {
            WEB_TMS: FULL,
        },
    },
};
